import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { Event } from '@/lib/types';
import { PageHeader } from '@/components/layout/PageHeader';
import { useYetki } from '@/lib/yetki';
import { InviteLinkBox } from '@/components/ui/InviteLinkBox';
import { Button, Card, Input, Label, Select, Spinner, Textarea } from '@/components/ui';

export function EventEditPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    locationName: '',
    locationMapsUrl: '',
    locationDirections: '',
    accommodationInfo: '',
    latitude: '',
    longitude: '',
    prayerDistrictId: '',
    participantLimit: '',
    notes: '',
    status: 'published' as Event['status'],
  });

  const { data, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api<{ event: Event }>(`/events/${eventId}`),
    enabled: !!eventId,
  });

  const { yonetebilir, yukleniyor: yetkiYukleniyor } = useYetki(data?.event?.organizationId);

  useEffect(() => {
    const event = data?.event;
    if (!event) return;
    setForm({
      name: event.name,
      description: event.description ?? '',
      startDate: event.startDate ?? '',
      endDate: event.endDate ?? '',
      locationName: event.locationName ?? '',
      locationMapsUrl: event.locationMapsUrl ?? '',
      locationDirections: event.locationDirections ?? '',
      accommodationInfo: event.accommodationInfo ?? '',
      latitude: event.latitude != null ? String(event.latitude) : '',
      longitude: event.longitude != null ? String(event.longitude) : '',
      prayerDistrictId: event.prayerDistrictId ?? '',
      participantLimit: event.participantLimit ? String(event.participantLimit) : '',
      notes: event.notes ?? '',
      status: event.status,
    });
  }, [data]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!eventId) return;
    setLoading(true);
    setError('');
    try {
      await api(`/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          participantLimit: form.participantLimit ? parseInt(form.participantLimit) : undefined,
          /* Boş bırakılırsa gönderilmiyor: sunucu COALESCE ile mevcut
             değeri koruyor, kazara sıfırlanmıyor. */
          latitude: form.latitude.trim() ? Number(form.latitude) : undefined,
          longitude: form.longitude.trim() ? Number(form.longitude) : undefined,
          prayerDistrictId: form.prayerDistrictId.trim() || undefined,
        }),
      });
      navigate('/events');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    } finally {
      setLoading(false);
    }
  }

  if (isLoading || yetkiYukleniyor) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  /* Sayfa adresten doğrudan açılabiliyordu: düğmeyi gizlemek yetmiyor,
     kapı da kapanmalı. Sunucu zaten reddediyor; burada kullanıcı boş
     bir forma emek vermesin. */
  if (!yonetebilir) {
    return (
      <div className="space-y-4">
        <PageHeader title="Program Düzenle" backTo="/events" />
        <Card>
          <p className="text-sm text-muted-foreground">
            Bu sayfa program yöneticilerine açık. Programı görüntülemek için Programlar
            sayfasına dönebilirsiniz.
          </p>
        </Card>
      </div>
    );
  }

  const inviteCode = data?.event?.inviteCode ?? '';

  return (
    <div className="space-y-4">
      <PageHeader title="Program Düzenle" subtitle="Etkinlik bilgilerini güncelleyin" backTo="/events" />

      {inviteCode && (
        <InviteLinkBox
          inviteCode={inviteCode}
          label="Program Davet Linki (sadece bu program görünür)"
          onRegenerate={async () => {
            const res = await api<{ inviteCode: string }>(`/events/${eventId}/regenerate-invite`, {
              method: 'POST',
            });
            return res.inviteCode;
          }}
        />
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Program Adı</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={form.startDate}
                max={form.endDate || undefined}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div>
              <Label>Bitiş Tarihi</Label>
              <Input
                type="date"
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Konum Adı</Label>
            <Input
              value={form.locationName}
              onChange={(e) => setForm({ ...form, locationName: e.target.value })}
              placeholder="Barla / Çamdağı"
            />
          </div>
          <div>
            <Label>Google Maps Linki</Label>
            <Input
              value={form.locationMapsUrl}
              onChange={(e) => setForm({ ...form, locationMapsUrl: e.target.value })}
            />
          </div>
          {/* Hava durumu buradan besleniyor. Yer adı yetmiyor:
              "Çamdağı" haritaların yer veritabanında yok, en yakın
              köyün adı da 8 km ötede ve 800 metre aşağıda kalıyor —
              hava 3-4 derece şaşıyor. Google Haritalar'da yere sağ
              tıklayınca çıkan koordinat buraya yapıştırılıyor. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Enlem</Label>
              <Input
                type="number"
                step="any"
                placeholder="38.0908437"
                value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              />
            </div>
            <div>
              <Label>Boylam</Label>
              <Input
                type="number"
                step="any"
                placeholder="30.7638461"
                value={form.longitude}
                onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              />
            </div>
          </div>
          {/* Namaz vakitleri Diyanet'in takviminden geliyor ve ilçe
              bazında veriliyor. Barla, Eğirdir'e bağlı: 9526. */}
          <div>
            <Label>Diyanet İlçe Kodu</Label>
            <Input
              value={form.prayerDistrictId}
              onChange={(e) => setForm({ ...form, prayerDistrictId: e.target.value })}
              placeholder="9526 (Eğirdir)"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Namaz vakitleri bu ilçeye göre gösterilir. Boş bırakılırsa vakit satırı çıkmaz.
            </p>
          </div>
          <div>
            <Label>Yol Tarifi</Label>
            <Textarea
              value={form.locationDirections}
              onChange={(e) => setForm({ ...form, locationDirections: e.target.value })}
            />
          </div>
          <div>
            <Label>Konaklama Bilgisi</Label>
            <Textarea
              value={form.accommodationInfo}
              onChange={(e) => setForm({ ...form, accommodationInfo: e.target.value })}
            />
          </div>
          <div>
            <Label>Katılımcı Limiti</Label>
            <Input
              type="number"
              value={form.participantLimit}
              onChange={(e) => setForm({ ...form, participantLimit: e.target.value })}
            />
          </div>
          <div>
            <Label>Notlar</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div>
            <Label>Durum</Label>
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Event['status'] })}>
              <option value="draft">Taslak</option>
              <option value="published">Yayında</option>
              <option value="archived">Arşiv</option>
            </Select>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Spinner /> : 'Kaydet'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
